import { z } from 'zod';

// AIDEV-NOTE: Tipos compartilhados para dashboards — consumidos por client e server

export const KpiMetricSchema = z.object({
  label: z.string(),
  value: z.number(),
  unit: z.enum(['currency', 'number', 'percent']),
  delta: z.number().optional() // variacao % vs periodo anterior
});
export type KpiMetric = z.infer<typeof KpiMetricSchema>;

export const AlertItemSchema = z.object({
  tipo: z.enum(['erro_sync', 'lead_estagnado', 'meta_baixa', 'visita_atrasada']),
  mensagem: z.string(),
  entidadeId: z.string(),
  url: z.string().optional()
});
export type AlertItem = z.infer<typeof AlertItemSchema>;

export const ClienteVisitaItemSchema = z.object({
  clienteErpId: z.number(),
  nome: z.string(),
  lat: z.number(),
  lng: z.number(),
  horaPrevista: z.string().optional(),
  status: z.enum(['pendente', 'concluida', 'cancelada'])
});
export type ClienteVisitaItem = z.infer<typeof ClienteVisitaItemSchema>;

export const VendedorKpiSchema = z.object({
  vendedorId: z.string(),
  vendedorErpId: z.number().optional(),
  nome: z.string(),
  totalVendas: z.number(),
  pedidosCount: z.number(),
  metaMensal: z.number().optional(),
  metaPercent: z.number().nullable()
});
export type VendedorKpi = z.infer<typeof VendedorKpiSchema>;

export const SyncEntityStatusSchema = z.object({
  entidade: z.string(),
  ultimaSync: z.string().nullable(),
  status: z.enum(['sincronizado', 'pendente', 'erro'])
});
export type SyncEntityStatus = z.infer<typeof SyncEntityStatusSchema>;

export const SyncLogEntrySchema = z.object({
  id: z.string().uuid(),
  entidade: z.string(),
  direcao: z.string(),
  status: z.string(),
  iniciadoEm: z.string(),
  registrosProcessados: z.number().nullable()
});
export type SyncLogEntry = z.infer<typeof SyncLogEntrySchema>;

// --- Tipos para gráficos ---

export const VendaHistoricoItemSchema = z.object({
  label: z.string(), // "Mai/25"
  mes: z.number(),
  ano: z.number(),
  total: z.number()
});
export type VendaHistoricoItem = z.infer<typeof VendaHistoricoItemSchema>;

export const VendaDiaItemSchema = z.object({
  dia: z.number(),
  total: z.number()
});
export type VendaDiaItem = z.infer<typeof VendaDiaItemSchema>;

export const TopClienteItemSchema = z.object({
  nome: z.string(),
  total: z.number()
});
export type TopClienteItem = z.infer<typeof TopClienteItemSchema>;

export const VendaVendedorItemSchema = z.object({
  nome: z.string(),
  total: z.number(),
  pedidos: z.number()
});
export type VendaVendedorItem = z.infer<typeof VendaVendedorItemSchema>;

export const GeoEstadoItemSchema = z.object({
  uf: z.string().min(2).max(2),
  nome: z.string(),
  total: z.number(),
  pedidos: z.number()
});
export type GeoEstadoItem = z.infer<typeof GeoEstadoItemSchema>;

export const GeoCidadeItemSchema = z.object({
  cidade: z.string(),
  total: z.number(),
  pedidos: z.number(),
  percentual: z.number()
});
export type GeoCidadeItem = z.infer<typeof GeoCidadeItemSchema>;

// --- Dashboard por perfil ---

export const DashboardVendedorDataSchema = z.object({
  kpis: z.array(KpiMetricSchema),
  goalPercent: z.number().nullable(),
  alertas: z.array(AlertItemSchema),
  visitasMes: z.number(),
  vendasHistorico: z.array(VendaHistoricoItemSchema),
  vendasDia: z.array(VendaDiaItemSchema),
  topClientes: z.array(TopClienteItemSchema)
});
export type DashboardVendedorData = z.infer<typeof DashboardVendedorDataSchema>;

export const DashboardRepresentanteDataSchema = z.object({
  visitasHoje: z.number(),
  visitasConcluidas: z.number(),
  kmPercorrido: z.number(),
  metaPercent: z.number().nullable(),
  alertas: z.array(AlertItemSchema)
});
export type DashboardRepresentanteData = z.infer<typeof DashboardRepresentanteDataSchema>;

export const DashboardGerenteDataSchema = z.object({
  teamRanking: z.array(VendedorKpiSchema),
  pipelineTotal: z.number(),
  alertas: z.array(AlertItemSchema)
});
export type DashboardGerenteData = z.infer<typeof DashboardGerenteDataSchema>;

export const DashboardAdminDataSchema = z.object({
  syncStatus: z.array(SyncEntityStatusSchema),
  erros24h: z.number(),
  usuariosAtivos: z.number(),
  // Métricas de vendas (todos os vendedores)
  kpis: z.array(KpiMetricSchema),
  visitasMes: z.number(),
  vendasHistorico: z.array(VendaHistoricoItemSchema),
  vendasDia: z.array(VendaDiaItemSchema),
  topClientes: z.array(TopClienteItemSchema),
  vendasPorVendedor: z.array(VendaVendedorItemSchema)
});
export type DashboardAdminData = z.infer<typeof DashboardAdminDataSchema>;
